import CoreAuth from "../core/auth";
import {deleteStorageKey, getStorageValue, setStorageValue, STORAGE_TYPE} from 'Core/storage';
import {
	access_token_storage_field_name,
	access_token_storage_type,
	auth_api,
	auth_api_endpoints, auth_api_handler_router_path,
	auth_api_request_type,
	auth_api_response_type,
	auth_broadcast_channel,
	auth_broadcast_message_logout, auth_code_verifier_storage_field_name,
	current_user_storage_field_name,
	decoded_access_token_storage_field_name,
	refresh_token_storage_field_name,
	refresh_token_storage_type,
	refreshing_tokens_storage_field_name,
	temporary_access_token_storage_field_name
} from 'Config/auth';
import {acl_storage_type, acl_storage_var} from "Config/acl";
import {
	io_default_request_options,
	io_pause_requests_storage_field_name,
	io_use_country_request_processor_for_auth, json_response_error_code_consent
} from "Config/io";
import {REQUEST_TYPE, RESPONSE_DATA_TYPE} from 'Core/io/const';
import {getString} from "Core/helpers/data";
import {buildRequest, getIOUrl, rawRequest} from 'Core/io/helper';
import {IoError, ResponseCodeError} from 'Core/errors';
import {areTokensRefreshing, refreshTokensWait} from 'Core/auth/helper';
import StandardJsonResponseError from "../core/errors/StandardJsonResponseError";
import {app_id, user_activity_storage_path} from 'Config/app';
import {translate} from 'Core/i18n';
import {isSuccessful} from 'Core/helpers/io';
import {getUrl, redirectToUrl} from 'Core/helpers/url';
import {get} from 'lodash';

/**
 * App auth class
 * @note This class must extend CoreAuth class, but it does not have to implement any changes if no custom functionality
 * is needed.
 */
class Auth extends CoreAuth {
	/**
	 * Ping method for authorized user
	 *
	 * @param {Function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as
	 * an argument.
	 * @param {boolean} [tryTokenRefresh=false] - Flag that specifies this function will try to refresh the token if ping
	 * fails (returns 401). This will pause all other requests until tokens refresh request is done.
	 * @param {object} [options] - Other request options that will be sent. Default value is retrieved form IO config
	 * ("/src/config/io.js").
	 * @param {Headers} [headers=null] - Request headers.
	 * @param {CoreAuth} [_this=this] - Current class in the class tree. This is used when extending the CoreAuth class
	 * and overwriting this method to make sure that other overwritten child methods will be used properly.
	 * @return {Promise<any>} Promise that resolves if ping was successful. Errors should be handled by response
	 * processors and the function that calls this method, not here.
	 * 
	 * @override Overridden to support consent errors and add the 'locale' request processor.
	 */
	static authorizedPing = (
		abortCallback, tryTokenRefresh = false, options = io_default_request_options, headers = null, _this = this
	) => {
		let requestProcessors = ['auth', 'csrfToken', 'locale'];
		if (io_use_country_request_processor_for_auth) requestProcessors.push('country');
		let responseProcessors = ['error'];
		if (auth_api_response_type === RESPONSE_DATA_TYPE.JSON) {
			responseProcessors.push('data');
			responseProcessors.push('standardJsonError');
		}

		// Get API to use
		const api = (
			getString(auth_api_endpoints.authorized_ping, 'api') ? auth_api_endpoints.authorized_ping.api : auth_api
		);

		return rawRequest({
			type: auth_api_request_type,
			url: getIOUrl(api, auth_api_endpoints.authorized_ping.path),
			api: api,
			endpoint: auth_api_endpoints.authorized_ping.path,
			data: {...auth_api_endpoints.authorized_ping.params},
			method: auth_api_endpoints.authorized_ping.method,
			autoToken: true,
			options,
			headers,
			responseType: auth_api_response_type,
			canPause: false,
			requestProcessors,
			responseProcessors,
			abortCallback
		})
			// Try to refresh tokens if 'tryTokenRefresh' flag is true and ping returns the 401 response.
			.catch(error => {
				// Consent StandardJsonResponseError is not actually an error so just return the resolved promise since user
				// is actually logged in. Consent dialog will be shown by the standard JSON error response processor.
				// @override
				if (
					error instanceof StandardJsonResponseError && 
					getString(error, 'response.errorCode') === json_response_error_code_consent
				) {
					return Promise.resolve();
				} else if (error instanceof ResponseCodeError && error.code === 401 && tryTokenRefresh) {
					return _this.refreshTokens(abortCallback, undefined, undefined, _this)
						.then(refreshTokenResponse => {
							// If tokens are already refreshing
							if (refreshTokenResponse === 'tokens_already_refreshing') {
								// Wait for tokens to refresh and repeat the ping request without trying to refresh tokens. 
								// Return the result from the repeated ping request.
								return refreshTokensWait()
									.then(() => _this.authorizedPing(abortCallback, false, options, headers, _this));
							}
							// If tokens refreshed successfully
							else {
								// Repeat the ping request to be sure that tokens were refreshed properly. Return the result 
								// from the repeated ping request.
								return _this.authorizedPing(abortCallback, false, options, headers, _this);
							}
						})
						// If refreshing tokens failed, throw the original ping request error
						.catch(() => { throw error; });
				}
				throw error;
			});
	}

	/**
	 * Check if user is logged in
	 * @note This method is used to log out the user after some period of inactivity.
	 *
	 * @param {Function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as
	 * an argument.
	 * @param {boolean} [tryTokenRefresh=false] - Flag that specifies this function will try to refresh the token if ping
	 * fails (returns 401). This will pause all other requests until tokens refresh request is done.
	 * @param {object} [options] - Other request options that will be sent. Default value is retrieved form IO config
	 * ("/src/config/io.js").
	 * @param {Headers} [headers=null] - Request headers.
	 * @param {CoreAuth} [_this=this] - Current class in the class tree. This is used when extending the CoreAuth class
	 * and overwriting this method to make sure that other overwritten child methods will be used properly.
	 * @return {Promise<any>} Promise that resolves if ping was successful. Errors should be handled by response
	 * processors and the function that calls this method, not here.
	 *
	 * @override Overridden to add the 'locale' request processor.
	 */
	static checkLogin(
		abortCallback, tryTokenRefresh = false, options = io_default_request_options, headers = null, _this = this
	) {
		let requestProcessors = ['auth', 'csrfToken', 'locale'];
		if (io_use_country_request_processor_for_auth) requestProcessors.push('country');
		let responseProcessors = ['error'];
		if (auth_api_response_type === RESPONSE_DATA_TYPE.JSON) {
			responseProcessors.push('data');
			responseProcessors.push('standardJsonError');
		}

		// Get API to use
		const api = (
			getString(auth_api_endpoints.check_login, 'api') ? auth_api_endpoints.check_login.api : auth_api
		);

		return rawRequest({
			type: auth_api_request_type,
			url: getIOUrl(api, auth_api_endpoints.check_login.path),
			api: api,
			endpoint: auth_api_endpoints.check_login.path,
			data: {...auth_api_endpoints.check_login.params},
			method: auth_api_endpoints.check_login.method,
			autoToken: true,
			options,
			headers,
			responseType: auth_api_response_type,
			canPause: false,
			requestProcessors,
			responseProcessors,
			abortCallback
		})
			// Try to refresh tokens if 'tryTokenRefresh' flag is true and ping returns the 401 response.
			.catch(error => {
				if (error instanceof ResponseCodeError && error.code === 401 && tryTokenRefresh) {
					return _this.refreshTokens(abortCallback, undefined, undefined, _this)
						.then(refreshTokenResponse => {
							// If tokens are already refreshing
							if (refreshTokenResponse === 'tokens_already_refreshing') {
								// Wait for tokens to refresh and repeat the check request without trying to refresh tokens. 
								// Return the result from the repeated check request.
								return refreshTokensWait()
									.then(() => _this.checkLogin(abortCallback, false, options, headers, _this));
							}
							// If tokens refreshed successfully
							else {
								// Repeat the check request to be sure that tokens were refreshed properly. Return the result 
								// from the repeated check request.
								return _this.checkLogin(abortCallback, false, options, headers, _this);
							}
						})
						// If refreshing tokens failed, throw the original ping request error
						.catch(() => { throw error; });
				}
				throw error;
			})
			.catch(error => {
				//console.log('Logout by auth check login method! Check login failed.', error);
				return _this.logout().then();
			});
	}

	/**
	 * Generate authorization code using authorization code request JWS token.
	 *
	 * @param {Function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as
	 * an argument.
	 * @param {string} authorizationCodeRequestJws - Authorization code request JWS (usually received through URL).
	 * @param {object} [options] - Other request options that will be sent. Default value is retrieved form IO config
	 * ("/src/config/io.js").
	 * @param {Headers} [headers=null] - Request headers.
	 * @return {Promise} Return a request error (400 Bad Request or 500 Internal Server Error) which should be handled by
	 * response processors or redirects if successful.
	 *
	 * @override Overridden to add the 'locale' request processor.
	 */
	static generateAuthorizationCode = (
		abortCallback, authorizationCodeRequestJws, options = io_default_request_options, headers = null
	) => {
		let requestProcessors = ['auth', 'csrfToken', 'locale'];
		if (io_use_country_request_processor_for_auth) requestProcessors.push('country');
		const responseProcessors = ['error', 'auth'];
		if (auth_api_response_type === RESPONSE_DATA_TYPE.JSON) {
			responseProcessors.push('data');
			responseProcessors.push('standardJsonError');
		}

		// Get API to use
		const api = (
			getString(auth_api_endpoints.generate_authorization_code, 'api') ?
				auth_api_endpoints.generate_authorization_code.api :
				auth_api
		);

		return rawRequest({
			type: auth_api_request_type,
			url: getIOUrl(api, auth_api_endpoints.generate_authorization_code.path),
			api: api,
			endpoint: auth_api_endpoints.generate_authorization_code.path,
			data: {
				authorizationCodeRequestJws: authorizationCodeRequestJws,
				...auth_api_endpoints.generate_authorization_code.params
			},
			method: auth_api_endpoints.generate_authorization_code.method,
			autoToken: true,
			options,
			headers,
			responseType: auth_api_response_type,
			canPause: false,
			requestProcessors,
			responseProcessors,
			abortCallback
		}).then(responseData => {
			if (isSuccessful(responseData)) {
				const url = getString(responseData, 'data');
				if (url) redirectToUrl(url);
				return responseData;
			}
		});
	}

	/**
	 * Try to refresh tokens using the refresh token
	 *
	 * @param {Function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as
	 * an argument.
	 * @param {object} [options] - Other request options that will be sent. Default value is retrieved form IO config
	 * ("/src/config/io.js").
	 * @param {Headers} [headers=null] - Request headers.
	 * @param {CoreAuth} [_this=this] - Current class in the class tree. This is used when extending the CoreAuth class
	 * and overwriting this method to make sure that other overwritten child methods will be used properly.
	 * @return {Promise<Object|'tokens_already_refreshing'>} Returns a promise that resolves with new tokens or a
	 * 'tokens_already_refreshing' string if refresh tokens is called during another token refresh. Promise will reject
	 * if tokens could not be refreshed for any reason.
	 *
	 * @override Overridden to add the 'locale' request processor.
	 */
	static refreshTokens = (abortCallback, options = io_default_request_options, headers = null, _this = this) => {
		if (areTokensRefreshing()) {
			return Promise.resolve('tokens_already_refreshing');
		} else {
			// Set refreshing tokens flag
			setStorageValue(refreshing_tokens_storage_field_name, '1', STORAGE_TYPE.LOCAL);

			let requestProcessors = ['csrfToken', 'locale'];
			if (io_use_country_request_processor_for_auth) requestProcessors.push('country');
			let responseProcessors = ['error'];
			if (auth_api_response_type === RESPONSE_DATA_TYPE.JSON) {
				responseProcessors.push('data');
			}

			// Get API to use
			const api = (
				getString(auth_api_endpoints.refresh_tokens, 'api') ?
					auth_api_endpoints.refresh_tokens.api :
					auth_api
			);

			return rawRequest({
				type: REQUEST_TYPE.STANDARD,
				url: getIOUrl(api, auth_api_endpoints.refresh_tokens.path),
				api: api,
				endpoint: auth_api_endpoints.refresh_tokens.path,
				data: {
					...auth_api_endpoints.refresh_tokens.params,
					code: getStorageValue(refresh_token_storage_field_name, refresh_token_storage_type),
					client_id: app_id
				},
				method: auth_api_endpoints.refresh_tokens.method,
				autoToken: true,
				options,
				headers,
				responseType: auth_api_response_type,
				// @important Refresh tokens request cannot be paused because it will pause all other requests until it's 
				// done so whichever request initiated the pause will not be able to resume and unpause effectively pausing
				// the whole app indefinitely.
				canPause: false, // DO NOT CHANGE THIS!
				requestProcessors,
				responseProcessors,
				abortCallback
			})
				.then(data => {
					const isError = (!data || data.hasOwnProperty('error'));
					if (isError) {
						throw new IoError(
							translate(getString(data, 'error'), 'Auth.authorize.errors'),
							getIOUrl(api, auth_api_endpoints.refresh_tokens.path),
							api,
							auth_api_endpoints.refresh_tokens.path,
							buildRequest(REQUEST_TYPE.STANDARD, auth_api_endpoints.refresh_tokens.method, {
								...auth_api_endpoints.refresh_tokens.params,
								code: getStorageValue(refresh_token_storage_field_name, refresh_token_storage_type),
								client_id: app_id
							}, options, headers),
							auth_api_response_type,
							abortCallback
						);
					} else {
						// Clear refreshing tokens flag
						setStorageValue(refreshing_tokens_storage_field_name, '0', STORAGE_TYPE.LOCAL);

						// Store newly refreshed tokens
						_this.storeAccessToken(get(data, 'access_token'));
						_this.storeRefreshToken(get(data, 'refresh_token'));

						// Call security init API to handle all server-side generated cookies
						return _this.securityInit(abortCallback).then(() => data);
					}
				})
				.catch(error => {
					// Clear refreshing tokens flag
					setStorageValue(refreshing_tokens_storage_field_name, '0', STORAGE_TYPE.LOCAL);

					throw error;
				});
		}
	}

	/**
	 * Try to refresh tokens using the authorization code
	 *
	 * @param {Function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as
	 * an argument.
	 * @param {string} authorizationCode - Authorization code.
	 * @param {object} [options] - Other request options that will be sent. Default value is retrieved form IO config
	 * ("/src/config/io.js").
	 * @param {Headers} [headers=null] - Request headers.
	 * @return {Promise<Object>} Returns a promise that resolves to the 'token' API response or rejects on error.
	 *
	 * @override Overridden to add the 'locale' request processor.
	 */
	static refreshTokensWithAuthorizationCode = (
		abortCallback, authorizationCode, options = io_default_request_options, headers = null
	) => {
		let requestProcessors = ['csrfToken', 'locale'];
		if (io_use_country_request_processor_for_auth) requestProcessors.push('country');
		let responseProcessors = ['error'];
		if (auth_api_response_type === RESPONSE_DATA_TYPE.JSON) {
			responseProcessors.push('data');
		}

		// Get API to use
		const api = (
			getString(auth_api_endpoints.refresh_tokens_with_authorization_code, 'api') ?
				auth_api_endpoints.refresh_tokens_with_authorization_code.api :
				auth_api
		);

		return rawRequest({
			type: REQUEST_TYPE.STANDARD,
			url: getIOUrl(api, auth_api_endpoints.refresh_tokens_with_authorization_code.path),
			api: api,
			endpoint: auth_api_endpoints.refresh_tokens_with_authorization_code.path,
			data: {
				...auth_api_endpoints.refresh_tokens_with_authorization_code.params,
				code: authorizationCode,
				redirect_uri: getUrl(auth_api_handler_router_path),
				client_id: app_id,
				code_verifier: getStorageValue(auth_code_verifier_storage_field_name, STORAGE_TYPE.SESSION)
			},
			method: auth_api_endpoints.generate_authorization_code.method,
			autoToken: true,
			options,
			headers,
			responseType: auth_api_response_type,
			canPause: false,
			requestProcessors,
			responseProcessors,
			abortCallback
		}).then(data => {
			const isError = (!data || data.hasOwnProperty('error'));
			if (isError) {
				throw new IoError(
					translate(getString(data, 'error'), 'Auth.authorize.errors'),
					getIOUrl(api, auth_api_endpoints.refresh_tokens.path),
					api,
					auth_api_endpoints.refresh_tokens.path,
					buildRequest(REQUEST_TYPE.STANDARD, auth_api_endpoints.refresh_tokens.method, {
						...auth_api_endpoints.refresh_tokens.params,
						code: getStorageValue(refresh_token_storage_field_name, refresh_token_storage_type),
						client_id: app_id
					}, options, headers),
					auth_api_response_type,
					abortCallback
				);
			} else {
				return data;
			}
		});
	}

	/**
	 * Call the API server security init method
	 * @description This should create httpOnly cookie for storing signature portion of the access cookie and a csrfToken
	 * cookie (security items for short).
	 * @note Temporarily stored access token will be used to make the API call. Make sure you stored it properly before
	 * calling this method.
	 *
	 * @param {Function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as
	 * an argument. This will not be used but is specified as param so that the standard 'executeAbortableAction' method,
	 * used by all components extending the abstract BaseComponent, will work properly.
	 * @param {object} [options] - Other request options that will be sent. Default value is retrieved form IO config
	 * ("/src/config/io.js").
	 * @param {Headers} [headers=null] - Request headers.
	 * @return {Promise<any>} Promise that resolves successfully if everything went well and security items were created
	 * or rejects if it didn't.
	 *
	 * @override Overridden to add the 'locale' request processor.
	 */
	static securityInit = (abortCallback, options = io_default_request_options, headers = null) => {
		let requestProcessors = ['auth', 'csrfToken', 'locale'];
		if (io_use_country_request_processor_for_auth) requestProcessors.push('country');
		let responseProcessors = ['error'];
		if (auth_api_response_type === RESPONSE_DATA_TYPE.JSON) {
			responseProcessors.push('data');
			responseProcessors.push('standardJsonError');
		}

		// Get API to use
		const api = (
			getString(auth_api_endpoints.security_init, 'api') ?
				auth_api_endpoints.security_init.api :
				auth_api
		);

		return rawRequest({
			type: auth_api_request_type,
			url: getIOUrl(api, auth_api_endpoints.security_init.path),
			api: api,
			endpoint: auth_api_endpoints.security_init.path,
			data: {
				access_token: getStorageValue(temporary_access_token_storage_field_name, STORAGE_TYPE.MEMORY),
				...auth_api_endpoints.security_init.params,
			},
			method: auth_api_endpoints.security_init.method,
			autoToken: true,
			options,
			headers,
			responseType: auth_api_response_type,
			canPause: false,
			requestProcessors,
			responseProcessors,
			abortCallback
		});
	}
	
	/**
	 * Logout the user
	 * @description This method will just remove tokens from storage.
	 * @return {Promise<void>}
	 *
	 * @override Deleted some additional cookies.
	 */
	static logout = () => {
		deleteStorageKey(io_pause_requests_storage_field_name, STORAGE_TYPE.LOCAL);
		deleteStorageKey(temporary_access_token_storage_field_name, STORAGE_TYPE.MEMORY);
		deleteStorageKey(access_token_storage_field_name, access_token_storage_type);
		deleteStorageKey(refresh_token_storage_field_name, refresh_token_storage_type);
		deleteStorageKey(refreshing_tokens_storage_field_name, STORAGE_TYPE.LOCAL);
		deleteStorageKey(decoded_access_token_storage_field_name, access_token_storage_type);
		deleteStorageKey(acl_storage_var, acl_storage_type);
		deleteStorageKey(current_user_storage_field_name, STORAGE_TYPE.LOCAL);
		deleteStorageKey(current_user_storage_field_name, STORAGE_TYPE.REDUX);
		deleteStorageKey(user_activity_storage_path, STORAGE_TYPE.LOCAL);
		
		deleteStorageKey('studies_list_advanced_search_visible', STORAGE_TYPE.SESSION);
		deleteStorageKey('do_not_show_password_expiration', STORAGE_TYPE.SESSION);
		
		// Broadcast logout so that all other opened tabs that need to will redirect to login page
		const bChannel = new BroadcastChannel(auth_broadcast_channel);
		bChannel.postMessage(auth_broadcast_message_logout);
		bChannel.close();
		
		return Promise.resolve();
	}
}

export default Auth;